import React, { useState, useEffect } from 'react';


const ZodiacWheel = ({ onPlanetSelect, selectedPlanets, planetPositions }) => {
  const [hoveredPlanet, setHoveredPlanet] = useState(null);
  const [wheelRotation, setWheelRotation] = useState(0);

  const zodiacSigns = [
    { name: 'Aries', symbol: '♈', angle: 0, color: '#FF6B6B' },
    { name: 'Taurus', symbol: '♉', angle: 30, color: '#4ECDC4' },
    { name: 'Gemini', symbol: '♊', angle: 60, color: '#45B7D1' },
    { name: 'Cancer', symbol: '♋', angle: 90, color: '#96CEB4' },
    { name: 'Leo', symbol: '♌', angle: 120, color: '#FFEAA7' },
    { name: 'Virgo', symbol: '♍', angle: 150, color: '#DDA0DD' },
    { name: 'Libra', symbol: '♎', angle: 180, color: '#98D8C8' },
    { name: 'Scorpio', symbol: '♏', angle: 210, color: '#F7DC6F' },
    { name: 'Sagittarius', symbol: '♐', angle: 240, color: '#BB8FCE' },
    { name: 'Capricorn', symbol: '♑', angle: 270, color: '#85C1E9' },
    { name: 'Aquarius', symbol: '♒', angle: 300, color: '#F8C471' },
    { name: 'Pisces', symbol: '♓', angle: 330, color: '#82E0AA' }
  ];

  const planets = [
    { name: 'Sun', symbol: '☉', position: 45, color: '#F59E0B', icon: 'Sun' },
    { name: 'Moon', symbol: '☽', position: 120, color: '#8B5CF6', icon: 'Moon' },
    { name: 'Mercury', symbol: '☿', position: 75, color: '#06B6D4', icon: 'Zap' },
    { name: 'Venus', symbol: '♀', position: 90, color: '#EC4899', icon: 'Heart' },
    { name: 'Mars', symbol: '♂', position: 180, color: '#EF4444', icon: 'Flame' },
    { name: 'Jupiter', symbol: '♃', position: 240, color: '#10B981', icon: 'Crown' },
    { name: 'Saturn', symbol: '♄', position: 300, color: '#6B7280', icon: 'Shield' },
    { name: 'Uranus', symbol: '♅', position: 15, color: '#3B82F6', icon: 'Sparkles' },
    { name: 'Neptune', symbol: '♆', position: 210, color: '#8B5CF6', icon: 'Waves' },
    { name: 'Pluto', symbol: '♇', position: 330, color: '#7C3AED', icon: 'Circle' }
  ];

  const circleOfFifths = [
    { note: 'C', angle: 0, frequency: 261.63 },
    { note: 'G', angle: 30, frequency: 392.00 },
    { note: 'D', angle: 60, frequency: 293.66 },
    { note: 'A', angle: 90, frequency: 440.00 },
    { note: 'E', angle: 120, frequency: 329.63 },
    { note: 'B', angle: 150, frequency: 493.88 },
    { note: 'F#', angle: 180, frequency: 369.99 },
    { note: 'C#', angle: 210, frequency: 277.18 },
    { note: 'G#', angle: 240, frequency: 415.30 },
    { note: 'D#', angle: 270, frequency: 311.13 },
    { note: 'A#', angle: 300, frequency: 466.16 },
    { note: 'F', angle: 330, frequency: 349.23 }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setWheelRotation(prev => (prev + 0.1) % 360);
    }, 100);
    return () => clearInterval(interval);
  }, []);

  const handlePlanetClick = (planet) => {
    onPlanetSelect(planet);
  };

  const getPlanetPosition = (angle, radius) => {
    const radian = (angle - 90) * (Math.PI / 180);
    return {
      x: Math.cos(radian) * radius,
      y: Math.sin(radian) * radius
    };
  };

  const isSelected = (planetName) => selectedPlanets.includes(planetName);

  return (
    <div className="relative w-full h-full flex items-center justify-center bg-gradient-to-br from-background via-surface to-background rounded-xl overflow-hidden">
      {/* Background Stars */}
      <div className="absolute inset-0">
        {[...Array(50)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-text-secondary rounded-full animate-gentle-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 2}s`
            }}
          />
        ))}
      </div>

      <svg
        width="100%"
        height="100%"
        viewBox="0 0 400 400"
        className="relative z-10"
      >
        {/* Outer Circle - Zodiac Signs */}
        <circle
          cx="200"
          cy="200"
          r="180"
          fill="none"
          stroke="rgba(248, 250, 252, 0.1)"
          strokeWidth="2"
        />

        {/* Middle Circle - Circle of Fifths */}
        <circle
          cx="200"
          cy="200"
          r="140"
          fill="none"
          stroke="rgba(74, 95, 191, 0.3)"
          strokeWidth="2"
        />

        {/* Inner Circle - Planet Orbits */}
        <circle
          cx="200"
          cy="200"
          r="100"
          fill="none"
          stroke="rgba(139, 92, 246, 0.2)"
          strokeWidth="1"
        />

        {/* Zodiac Signs */}
        {zodiacSigns.map((sign, index) => {
          const position = getPlanetPosition(sign.angle + wheelRotation * 0.1, 165);
          return (
            <g key={sign.name}>
              <text
                x={200 + position.x}
                y={200 + position.y}
                textAnchor="middle"
                dominantBaseline="middle"
                className="text-sm font-mono fill-text-secondary"
                style={{ fontSize: '16px' }}
              >
                {sign.symbol}
              </text>
              <text
                x={200 + position.x}
                y={200 + position.y + 20}
                textAnchor="middle"
                dominantBaseline="middle"
                className="text-xs fill-text-secondary"
                style={{ fontSize: '10px' }}
              >
                {sign.name}
              </text>
            </g>
          );
        })}

        {/* Circle of Fifths Notes */}
        {circleOfFifths.map((note, index) => {
          const position = getPlanetPosition(note.angle, 125);
          return (
            <g key={note.note}>
              <circle
                cx={200 + position.x}
                cy={200 + position.y}
                r="12"
                fill="rgba(74, 95, 191, 0.2)"
                stroke="#4A5FBF"
                strokeWidth="1"
              />
              <text
                x={200 + position.x}
                y={200 + position.y}
                textAnchor="middle"
                dominantBaseline="middle"
                className="text-xs font-mono fill-primary"
                style={{ fontSize: '12px' }}
              >
                {note.note}
              </text>
            </g>
          );
        })}

        {/* Planets */}
        {planets.map((planet, index) => {
          const position = getPlanetPosition(planet.position + wheelRotation * 0.5, 85);
          const selected = isSelected(planet.name);
          const hovered = hoveredPlanet === planet.name;
          
          return (
            <g key={planet.name}>
              {/* Planet Glow Effect */}
              {(selected || hovered) && (
                <circle
                  cx={200 + position.x}
                  cy={200 + position.y}
                  r="20"
                  fill={planet.color}
                  opacity="0.3"
                  className="animate-gentle-pulse"
                />
              )}
              
              {/* Planet Circle */}
              <circle
                cx={200 + position.x}
                cy={200 + position.y}
                r={selected ? "16" : "12"}
                fill={planet.color}
                stroke={selected ? "#F8FAFC" : "transparent"}
                strokeWidth="2"
                className="cursor-pointer transition-all duration-200 hover:stroke-accent"
                onClick={() => handlePlanetClick(planet)}
                onMouseEnter={() => setHoveredPlanet(planet.name)}
                onMouseLeave={() => setHoveredPlanet(null)}
              />
              
              {/* Planet Symbol */}
              <text
                x={200 + position.x}
                y={200 + position.y}
                textAnchor="middle"
                dominantBaseline="middle"
                className="text-xs font-mono fill-white pointer-events-none"
                style={{ fontSize: '10px' }}
              >
                {planet.symbol}
              </text>
              
              {/* Planet Name on Hover */}
              {hovered && (
                <text
                  x={200 + position.x}
                  y={200 + position.y - 25}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className="text-xs fill-text-primary font-medium"
                  style={{ fontSize: '12px' }}
                >
                  {planet.name}
                </text>
              )}
            </g>
          );
        })}

        {/* Center Logo */}
        <circle
          cx="200"
          cy="200"
          r="30"
          fill="rgba(74, 95, 191, 0.1)"
          stroke="#4A5FBF"
          strokeWidth="2"
        />
        <text
          x="200"
          y="200"
          textAnchor="middle"
          dominantBaseline="middle"
          className="text-lg font-heading fill-primary"
          style={{ fontSize: '16px' }}
        >
          ♪
        </text>
      </svg>

      {/* Legend */}
      <div className="absolute bottom-4 left-4 glass bg-surface/80 backdrop-blur-cosmic rounded-lg p-3">
        <div className="text-xs text-text-secondary mb-2 font-medium">Active Planets</div>
        <div className="flex flex-wrap gap-2">
          {selectedPlanets.map(planetName => {
            const planet = planets.find(p => p.name === planetName);
            return (
              <div key={planetName} className="flex items-center space-x-1">
                <div 
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: planet?.color }}
                />
                <span className="text-xs text-text-primary">{planetName}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ZodiacWheel;